import {animate, style, transition, trigger} from '@angular/animations';
import {NgClass, NgIf} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_TYPE,
  FvButtonComponent,
} from '@discocil/fv-components';
import {TranslateModule} from '@ngx-translate/core';
import {first, interval, timer} from 'rxjs';
import {environment} from 'src/environments/environment';
import {ENVIRONMENT_NAME} from 'src/environments/environment-name.enum';
import {HEADER_FLAG} from '../../enums/header-flags.enum';
import {randomFromArray} from '../../utils/array.utils';
import {ISplashArtConfig} from './interface/splashart.interface';
import {Logger} from 'src/app/core/interfaces/Logger';
import {BodyScrollManagerService} from '../../services/body-scroll-manager/body-scroll-manager.service';
import {FeatureFlagAdapter} from "@shared/infrastructure/adapters/feature-flag.adapter";

@Component({
  selector: 'app-splashart-loader',
  templateUrl: './splashart-loader.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, NgClass, NgIf, FvButtonComponent, TranslateModule],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({opacity: 0}),
        animate('200ms ease-out', style({opacity: 1})),
      ]),

      transition(':leave', [animate('100ms ease-in', style({opacity: 0}))]),
    ]),
  ],
})
/**
 * Component that manages the splash art loader screen with loading texts and animations.
 * Provides functionality to display loading screens with custom configurations.
 */
export class SplashartLoaderComponent implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagAdapter);
  private readonly logger = inject(Logger);
  private readonly bodyScrollManager = inject(BodyScrollManagerService);

  protected isOpen = signal<boolean>(false);
  protected config = signal<ISplashArtConfig | null>(null);
  protected showPingAnimation = signal<boolean>(false);
  protected showPulseAnimation = signal<boolean>(false);
  protected isDevEnvironment = signal<boolean>(false);
  protected isHelperActivatedFlag = signal<boolean>(false);

  loadingText = signal<string>('');

  BUTTON_COLOR = BUTTON_COLOR;
  BUTTON_SIZE = BUTTON_SIZE;
  BUTTON_TYPE = BUTTON_TYPE;

  /**
   * Computed signal that determines the image URL for the splash screen.
   * Returns the custom image URL from config if provided, otherwise returns the default image path.
   *
   * @returns The URL path for the splash screen image
   */
  protected imageUrl = computed<string>(() => {
    const config = this.config();

    if (config?.imageUrl) {
      return config.imageUrl;
    }

    return '/assets/img/splash_screen.webp';
  });

  /**
   * Sets the loading text based on the provided configuration.
   * If random texts are enabled, it will cycle through the texts at the specified interval.
   * Otherwise, it sets the first text from the loading texts array.
   */
  private getLoadingText(): void {
    const config = this.config();

    if (config) {
      if (config.isShowingRandomTexts && config.randomTextsTimer) {
        this.loadingText.set(randomFromArray([...config.loadingTexts]));

        interval(config.randomTextsTimer)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            const text = randomFromArray([...config.loadingTexts]);

            return this.loadingText.set(text);
          });

        return;
      }

      return this.loadingText.set([...config.loadingTexts][0]);
    }
  }

  constructor() {
    this.setFeatureFlags();
    this.isDevEnvironment.set(
      environment.name === ENVIRONMENT_NAME.dev ||
      environment.name === ENVIRONMENT_NAME.alpha
    );
  }

  /**
   * Opens the splash art loader with the provided configuration.
   * Initializes the loading text and animations.
   * Disables body scroll on mobile devices.
   *
   * @param config - Configuration object for the splash art loader
   */
  open(config: ISplashArtConfig): void {
    this.config.set(config);
    this.isOpen.set(true);
    this.bodyScrollManager.disableScroll();
    this.getLoadingText();

    timer(500)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.showPulseAnimation.set(true);
      });
  }

  /**
   * Closes the splash art loader with an animation sequence.
   * Resets all state variables after the closing animation completes.
   * Enables body scroll on mobile devices after animations finish.
   */
  close(): void {
    this.showPulseAnimation.set(false);
    this.showPingAnimation.set(true);

    timer(500)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.config.set(null);
        this.isOpen.set(false);
        this.loadingText.set('');
        this.showPingAnimation.set(false);
        this.bodyScrollManager.enableScroll();
      });
  }

  /**
   * Sets up feature flags subscription and updates the helper flag state
   * when feature flags are loaded.
   */
  private setFeatureFlags(): void {
    this.featureFlagService.onFeatureFlagsLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isHelperActivatedFlag.set(
          this.featureFlagService.isFlagActivated(HEADER_FLAG.allowHealthHelper)
        );
      });
  }

  /**
   * Method to clean up resources.
   * No need to unsubscribe manually since we use takeUntilDestroyed,
   * but we implement OnDestroy for consistency and best practices.
   * Force enables scroll as emergency cleanup if component is destroyed while splash is open.
   */
  ngOnDestroy(): void {
    this.bodyScrollManager.forceEnableScroll();
    this.logger.debug('[SplashartLoaderComponent] cleaning up resources');
  }
}
